// Polyfill for process object in browser environment
if (typeof window !== 'undefined' && typeof process === 'undefined') {
  (window as any).process = {
    env: {},
    browser: true,
    version: '',
    versions: {},
    platform: 'browser'
  };
}

import React from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { createWeb3Modal } from '@web3modal/wagmi/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { WagmiProvider } from 'wagmi';
import { config } from './config/wagmi';
import { 
  Chart as ChartJS, 
  ArcElement, 
  Tooltip, 
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  TimeScale,
  Title,
  Filler,
  BarElement,
  ScatterController
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import { autoUpdateService } from './services/autoUpdateService';

import App from './App';
import './index.css';

// Register Chart.js components - only register what's needed
ChartJS.register(
  ArcElement, 
  Tooltip, 
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  TimeScale,
  Title,
  Filler,
  BarElement,
  ScatterController
);

// Create Web3Modal with minimal configuration
createWeb3Modal({
  wagmiConfig: config,
  projectId: '30cc931753f26e660cc712092ad216d0',
  themeMode: 'dark',
  themeVariables: {
    '--w3m-accent': '#dc2626',
    '--w3m-border-radius-master': '0.5rem'
  }
});

// Create React Query client with optimized settings
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Enable caching for better performance
      cacheTime: 5 * 60 * 1000, // 5 minutes
      staleTime: 1 * 60 * 1000, // 1 minute
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      refetchOnMount: false
    }
  }
});

// Start auto-update service with reduced frequency
autoUpdateService.startAutoUpdate();

// Make prerendered data available globally
declare global {
  interface Window {
    __PRERENDERED_DATA__?: any;
  }
}

// Expose prerendered data from the server
if (typeof window !== 'undefined') {
  // Look for the prerendered data script tag
  const dataScript = document.getElementById('__PRERENDERED_DATA__');
  if (dataScript) {
    try {
      window.__PRERENDERED_DATA__ = JSON.parse(dataScript.textContent || '{}');
    } catch (e) {
      console.error('Failed to parse prerendered data:', e);
      window.__PRERENDERED_DATA__ = {};
    }
  } else {
    window.__PRERENDERED_DATA__ = {};
  }
}

const container = document.getElementById('root');

// Add meta tag to disable caching
const addNoCacheMetaTags = () => {
  // Add Cache-Control meta tag
  let metaCache = document.createElement('meta');
  metaCache.httpEquiv = 'Cache-Control';
  metaCache.content = 'no-cache, no-store, must-revalidate';
  document.head.appendChild(metaCache);
  
  // Add Pragma meta tag
  let metaPragma = document.createElement('meta');
  metaPragma.httpEquiv = 'Pragma';
  metaPragma.content = 'no-cache';
  document.head.appendChild(metaPragma);
  
  // Add Expires meta tag
  let metaExpires = document.createElement('meta');
  metaExpires.httpEquiv = 'Expires';
  metaExpires.content = '0';
  document.head.appendChild(metaExpires);
};

// Add no-cache meta tags
addNoCacheMetaTags();

// Use createRoot instead of hydrateRoot for better performance
createRoot(container!).render(
  <React.StrictMode>
    <WagmiProvider config={config}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </QueryClientProvider>
    </WagmiProvider>
  </React.StrictMode>
);